import { useParams, useSearchParams, useNavigate } from "react-router-dom"
import { gql } from "~/__generated__"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { TablePageLayout } from "~/layouts/table-page-layout"
import { GraphqlError } from "~/ui/errors"
import { toast } from "~/ui/use-toast"
import { UserInfoSection } from "./components/user-info-section"
import { ProfileHeader } from "./components/profile-header"
import { useProfileData } from "./hooks/use-profile-data"
import { ContactTagsAndNotesSection } from "./components/contact-tags-and-notes-section"

const CREATE_CONTACT_FROM_SEARCH_MUTATION = gql(/* GraphQL */ `
  mutation CreateContactFromSearch($input: ContactCreateFromSearchInput!) {
    contactCreateFromSearch(input: $input) {
      contact {
        id
      }
      userHandle
    }
  }
`)

const DELETE_CONTACT_MUTATION = gql(/* GraphQL */ `
  mutation DeleteContact($input: ContactDeleteInput!) {
    contactDelete(input: $input) {
      success
    }
  }
`)

export const UserProfileScreen = () => {
  const { handle } = useParams<{ handle: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const csrid = searchParams.get("csrid")
  const { loading, error, user, contact, isOwnProfile } = useProfileData(handle, csrid || undefined)

  const [createContactFromSearch, { loading: createContactFromSearchLoading }] = useSafeMutation(
    CREATE_CONTACT_FROM_SEARCH_MUTATION,
    {
      onCompleted: async (data) => {
        // Redirect to the handle-only URL to switch from search mode to contact mode
        const userHandle = data.contactCreateFromSearch.userHandle
        navigate(`/${userHandle}`, { replace: true })

        toast({
          title: "Contact Added",
          description: "User has been successfully added to your contacts.",
        })
      },
      onError: (error) => {
        console.error("Contact create error:", error)
        toast({
          title: "Add to Contacts Failed",
          description: "There was an error adding this user to your contacts. Please try again.",
          variant: "destructive",
        })
      },
    }
  )

  const [deleteContact, { loading: deleteContactLoading }] = useSafeMutation(
    DELETE_CONTACT_MUTATION,
    {
      onCompleted: () => {
        toast({
          title: "Contact Removed",
          description: "The contact has been successfully removed.",
        })
        navigate("/contacts")
      },
      onError: (error) => {
        console.error("Contact delete error:", error)
        toast({
          title: "Remove Contact Failed",
          description: "There was an error removing this contact. Please try again.",
          variant: "destructive",
        })
      },
    }
  )

  const handleAddToContacts = async () => {
    const csrid = searchParams.get("csrid")
    if (!csrid) {
      return
    }

    await createContactFromSearch({
      variables: { input: { contactFromSearchInput: { contactSearchId: csrid } } },
    })
  }

  const handleRemoveContact = async () => {
    if (!contact) {
      return
    }

    const userName = user?.fullName || user?.handle || "this contact"

    const confirmed = window.confirm(
      `Are you sure you want to remove ${userName}? This will remove ${userName} from your Contacts. You will have to search and re-add them back if you change your mind.`
    )

    if (!confirmed) {
      return
    }

    await deleteContact({
      variables: {
        input: {
          id: contact.id,
        },
      },
    })
  }

  if (loading) {
    return (
      <TablePageLayout>
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">Loading...</div>
        </div>
      </TablePageLayout>
    )
  }

  if (!user) {
    return (
      <TablePageLayout>
        <div className="flex items-center justify-center py-8">
          <div className="text-gray-500">
            User not found or you don't have permission to view this profile.
          </div>
        </div>
      </TablePageLayout>
    )
  }

  if (error) {
    return (
      <TablePageLayout>
        <GraphqlError error={error} />
      </TablePageLayout>
    )
  }

  return (
    <TablePageLayout padding={false}>
      <ProfileHeader
        user={user}
        isOwnProfile={isOwnProfile}
        contact={contact}
        onAddToContacts={handleAddToContacts}
        addToContactsLoading={createContactFromSearchLoading}
        onRemoveContact={handleRemoveContact}
        removeContactLoading={deleteContactLoading}
      />

      <div className="mx-auto max-w-2xl px-4">
        <div className="rounded-lg bg-white p-6 shadow-sm">
          <UserInfoSection user={user} isEditable={isOwnProfile} />

          {contact && <ContactTagsAndNotesSection contact={contact} />}
        </div>
      </div>
    </TablePageLayout>
  )
}
